// SmartPageRuntime — composes a smart page in either preview or
// document mode. The component is intentionally thin: it owns the
// loading of the doc instance (document mode), seeds the runtime
// state, wraps everything in the `RuntimeProvider`, then walks the
// page model and dispatches each block to `RuntimeBlockHost`.
//
// The same component is mounted by both branches of the Live Preview
// pane — internal/external pages use mode='preview' (no docId, no
// persistence), while document pages use mode='document' (loads the
// doc, merges saved block properties into the template).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { APP_BASE } from '@/config/basePath'
import { getHostBinding } from '@/config/hostBinding'
import { Loader2, MoreVertical, Pencil, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import type { Block, PageModel, Section, Viewport } from '@/pages/designers/page/types'
import {
  PageRuntimeContext, PageFieldErrorsContext, PageFieldRefsContext,
  type PageRuntime, type ActionState, type FieldErrors,
} from '@/pages/designers/page/actions/runtime'
import { runAction, buildResolver, type ActionCtx } from '@/pages/designers/page/actions/runner'
import { getPath, toDisplayString } from '@/pages/designers/page/actions/substitute'
import { isVisible, meOf, withMe } from '@/pages/designers/page/visibility'
import { resolveTheme, ensureThemeFontsLoaded } from '@/pages/designers/page/theme'
import { SectionWrapper } from '@twasta/page-runtime/ThemedPageShell'
import { resolveColorScheme, colorSchemeToCssVars } from '@/pages/designers/page/tokens'
import { getDocument } from '@/api/documents'
import { readerApiFor, submitAppForm } from '@/api/website'
import { ReaderApiContext } from '@twasta/page-runtime/readerBlocks'
import { PageDataBlocksContext } from '@twasta/page-runtime/commerceApi'
import { pageDataBlocksApiFor } from '@/api/website'
import type { FormStoreSubmission } from '@twasta/page-runtime/forms'
import {
  DOC_STATE_BLOCK_ID, ROOT_SCOPE,
  RuntimeProvider, useRuntime,
  type RuntimeMode,
} from './RuntimeContext'
import RuntimeBlockHost, { PageVarsContext, SimulatedViewportContext, useEntriesFor, useVisibilityVars } from './RuntimeBlockHost'
import { PopupLauncher, usePageCart, useAuthMenu } from '@twasta/page-runtime/renderers'
import { renderIdentifyFor } from '@twasta/page-runtime/identifyStep'
import { AddBlockRow, BlockPickerPortal } from './editAffordances'
import { SectionChromeContext } from './sectionChrome'
import type { DataAccess } from './dataBinding'
import { embedExecute } from '@/livepreview/embedDataApi'
import { useActionConfirm } from '@twasta/page-runtime/txn'
import { TerminalMode } from '@twasta/page-runtime/live/terminal'
import type { VarSource } from '@twasta/page-runtime/actions/substitute'

interface Props {
  page: PageModel
  mode: RuntimeMode
  docId: string | null
  /**
   * Project name (used by EmbedPreview when blocks delegate down to
   * `/api/livepreview/*`). Embed blocks need the project to resolve
   * the transaction object metadata.
   */
  project: string
  /**
   * Opt-in real-time collaboration (document mode only). Off by default;
   * when true, block-property edits sync through the shared Yjs gateway
   * instead of REST DocSync. See RuntimeProvider / useYjsDocument.
   */
  collab?: boolean
  /** Current viewer's roles, for block/section visibility gating. */
  userRoles?: string[]
  /**
   * How live data sources are fetched. Defaults to the in-app authenticated
   * preview ({mode:'preview'}). The Site preview passes {mode:'site'} so
   * fetches carry the end-user session and enforce object/action grants.
   */
  dataAccess?: DataAccess
  /** Re-fetch fresh page-bound embed tokens (site viewer). Passed through to
   * the RuntimeProvider so embeds can recover from an expired token. */
  refreshEmbedTokens?: () => Promise<Record<string, string> | null>
  /**
   * Extra page-vars seeded on top of the page's input defaults. Used when a
   * page is embedded inside another page so the host's arg-mapping can pass
   * values onto the embedded page's PageInput names. Overlays input defaults
   * but is overlaid by any saved document vars.
   */
  extraVars?: Record<string, unknown>
  /**
   * The page fills the browser viewport (the published-site viewer), so a
   * `width: "full"` section may break out of the content column to the
   * viewport edges. Off for every pane-hosted surface (designer previews,
   * thumbnails, the Run view's content area), where `100vw` would overflow
   * the pane — there a full section paints its band inside the column.
   */
  fullBleed?: boolean
  /**
   * The device a designer preview simulates by narrowing its frame. Device
   * visibility rules are then judged against it instead of the real window
   * width (see `SimulatedViewportContext`). Leave unset where the page fills
   * the browser.
   */
  viewport?: Viewport
}

/** Whether `width: "full"` sections may span the viewport on this surface. */
const FullBleedContext = React.createContext(false)

export default function SmartPageRuntime({ page, mode, docId, project, collab = false, userRoles, dataAccess, refreshEmbedTokens, extraVars, fullBleed = false, viewport }: Props) {
  const [docBlocks, setDocBlocks] = useState<{
    props: Record<string, Record<string, unknown>>
    addedIds: Set<string>
    addedBlocks: Record<string, import('@/api/documents').DocumentBlock>
    savedVars: Record<string, unknown>
    savedOrder: Record<string, string[]>
    savedDeleted: string[]
  } | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Seed page-vars: page.inputs defaults provide the baseline, then
  // saved vars from the doc's __doc_state__ block overlay on top so a
  // returning user sees the values they captured last session.
  const initialVars = useMemo(() => {
    const out: Record<string, unknown> = {}
    for (const inp of page.inputs || []) {
      // A publisher-BOUND input (a collection record's field) is a real value
      // even when empty — same rule as the static renderer's seedVars, so an
      // empty summary renders as nothing rather than a literal token.
      if (inp.default !== undefined && (inp.default !== '' || (inp as { bound?: boolean }).bound)) {
        let v: unknown = inp.default
        // A `type:"json"` input holds structured data (object/array). Its
        // default can arrive as a JSON string — notably when a page
        // `beforeLoad` bakes a `set_vars` list/object into the input default
        // (see runtime-engine `_bake_set_vars_into_inputs`, which json.dumps
        // it). Parse it so bindings (e.g. a table's `rows`) receive the real
        // array/object, mirroring how `coerceInputs` already parses json
        // inputs. Leave malformed strings untouched.
        if (inp.type === 'json' && typeof v === 'string') {
          try { v = JSON.parse(v) } catch { /* not JSON — keep the raw string */ }
        }
        out[inp.name] = v
      }
    }
    // Host-supplied arg-mapping (embedded page) overlays input defaults.
    if (extraVars) {
      for (const [k, v] of Object.entries(extraVars)) {
        if (v !== undefined) out[k] = v
      }
    }
    if (docBlocks?.savedVars) {
      Object.assign(out, docBlocks.savedVars)
    }
    return out
  }, [page.inputs, extraVars, docBlocks?.savedVars])

  // Document-mode: fetch persisted block instances.
  useEffect(() => {
    if (mode !== 'document' || !docId) {
      setDocBlocks({
        props: {}, addedIds: new Set(), addedBlocks: {},
        savedVars: {}, savedOrder: {}, savedDeleted: [],
      })
      return
    }
    let cancelled = false
    setLoadError(null)
    getDocument(docId).then(
      (detail) => {
        if (cancelled) return
        const templateIds = collectBlockIds(page)
        const props: Record<string, Record<string, unknown>> = {}
        const addedIds = new Set<string>()
        const addedBlocks: Record<string, import('@/api/documents').DocumentBlock> = {}
        let savedVars: Record<string, unknown> = {}
        let savedOrder: Record<string, string[]> = {}
        let savedDeleted: string[] = []
        for (const b of detail.blocks) {
          // The __doc_state__ row carries per-doc metadata (saved vars,
          // ordering, soft-deletes) — extract everything and skip it
          // from the block tree.
          if (b.id === DOC_STATE_BLOCK_ID) {
            const p = (b.properties || {}) as {
              vars?: unknown
              order?: unknown
              deleted?: unknown
            }
            if (p.vars && typeof p.vars === 'object') {
              savedVars = p.vars as Record<string, unknown>
            }
            if (p.order && typeof p.order === 'object') {
              const cleaned: Record<string, string[]> = {}
              for (const [scope, ids] of Object.entries(p.order as Record<string, unknown>)) {
                if (Array.isArray(ids)) {
                  cleaned[scope] = ids.filter((x): x is string => typeof x === 'string')
                }
              }
              savedOrder = cleaned
            }
            if (Array.isArray(p.deleted)) {
              savedDeleted = (p.deleted as unknown[]).filter((x): x is string => typeof x === 'string')
            }
            continue
          }
          props[b.id] = b.properties || {}
          if (!templateIds.has(b.id)) {
            addedIds.add(b.id)
            addedBlocks[b.id] = b
          }
        }
        setDocBlocks({ props, addedIds, addedBlocks, savedVars, savedOrder, savedDeleted })
      },
      (err: unknown) => {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String(err)
        setLoadError(msg || 'Failed to load document')
      },
    )
    return () => { cancelled = true }
  }, [mode, docId, page])

  if (mode === 'document' && loadError) {
    return <RuntimeErrorBox message={loadError} />
  }
  if (mode === 'document' && !docBlocks) {
    return <RuntimeLoadingBox label="Loading document…" />
  }

  return (
    <RuntimeProvider
      mode={mode}
      page={page}
      docId={docId}
      project={project}
      refreshEmbedTokens={refreshEmbedTokens}
      initialBlockProps={docBlocks?.props}
      initialAddedIds={docBlocks?.addedIds}
      initialAddedBlocks={docBlocks?.addedBlocks}
      initialVars={initialVars}
      initialOrder={docBlocks?.savedOrder}
      initialDeleted={docBlocks?.savedDeleted}
      collab={collab}
      userRoles={userRoles}
      dataAccess={dataAccess}
    >
      <FullBleedContext.Provider value={fullBleed}>
        <SimulatedViewportContext.Provider value={viewport}>
          <RuntimeBody page={page} />
        </SimulatedViewportContext.Provider>
      </FullBleedContext.Provider>
    </RuntimeProvider>
  )
}

function RuntimeBody({ page }: { page: PageModel }) {
  const runtime = useRuntime()
  // The page cart, booted once for the whole page. This component is inside
  // the runtime provider and is the one route every in-app smart page takes
  // (designer preview, live preview, the Run view, the website viewer, an
  // embedded page), so wiring it here covers all of them. Scoped by project
  // because they share the platform's single origin.
  //
  // The page's inputs come too, so a variable declared with a `cart` source
  // is filled from the basket — the route an embed's `recordVar` reads to
  // open the order already holding what the visitor chose.
  //
  // EXCEPT on a domain bound to a published SITE: that host is the site's own
  // origin, and its prerendered pages (the static bundle) keep the basket
  // unscoped. A page the bundle does not carry (checkout, account) falls back
  // to this viewer on the SAME origin, so scoping here would read a different
  // key than the product pages wrote — the basket shows, then "is empty" on
  // submit.
  // A designer preview SIMULATES a device frame; the kiosk overlay (fullscreen,
  // wake lock) belongs only to the real screen.
  const simulatedViewport = React.useContext(SimulatedViewportContext)
  const siteHost = getHostBinding()?.target === 'site'
  usePageCart(siteHost ? undefined : runtime.project, page.inputs, runtime.setVar)
  // Build a live action runtime bound to the smart-page vars store, then
  // expose it (plus field-error / field-ref registries) through the same
  // contexts the designer canvas uses. This makes `run_action` buttons and
  // `form` submit actions fire inside the smart-page runtime. The vars
  // resolver also surfaces `{{action.loading|error|status|<path>}}` tokens.
  const { pageRuntime: baseRuntime, fieldErrorsApi, fieldRefsApi, varsResolver, actionConfirmModal } = useActionRuntime()
  // Page forms store their submissions (and run their server-side
  // destination) through the transport that matches how this page is served.
  const { dataAccess, project } = runtime
  const pageObjName = page.obj_name || 'page'
  const pageRuntime = useMemo(() => ({
    ...baseRuntime,
    submitForm: (payload: FormStoreSubmission) =>
      submitAppForm(dataAccess, project, pageObjName, payload).catch((err: unknown) => {
        const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
        throw new Error(typeof detail === 'string' ? detail : 'Could not send — please try again')
      }),
  }), [baseRuntime, dataAccess, project, pageObjName])
  const readerApi = useMemo(() => readerApiFor(dataAccess, project), [dataAccess, project])
  // The storefront commerce blocks' server (checkout, my orders, reviews).
  // A block that needs an account (checkout) renders the host's identify-first
  // step, taken from the AuthMenuContext adapter the site viewer provides.
  const identifyAdapter = useAuthMenu()?.identify
  const dataBlocksApi = useMemo(() => {
    const base = pageDataBlocksApiFor(dataAccess, project, pageObjName, runtime.refreshEmbedTokens)
    if (!base || base.renderIdentify || !identifyAdapter) return base
    return { ...base, renderIdentify: renderIdentifyFor(identifyAdapter) }
  }, [dataAccess, project, pageObjName, runtime.refreshEmbedTokens, identifyAdapter])
  return (
    <PageDataBlocksContext.Provider value={dataBlocksApi}>
    <ReaderApiContext.Provider value={readerApi}>
    <PageRuntimeContext.Provider value={pageRuntime}>
      <PageFieldErrorsContext.Provider value={fieldErrorsApi}>
        <PageFieldRefsContext.Provider value={fieldRefsApi}>
          <PageVarsContext.Provider value={varsResolver}>
            {/* Empty editable blocks (document mode) show their placeholder.
                The designer canvas injects this rule too; the runtime needs
                its own copy since the canvas isn't mounted here — without it
                an empty document paragraph renders as a blank line. */}
            <style>{`
              [contenteditable][data-placeholder]:empty::before {
                content: attr(data-placeholder);
                color: var(--pg-text-secondary, var(--color-text-secondary, #94a3b8));
                opacity: 0.7;
                pointer-events: none;
              }
            `}</style>
            {runtime.mode === 'document' && <DocHeader />}
            <PageThemeFonts theme={page.theme} />
            <RuntimePageTree page={page} />
            {runtime.mode === 'document' && <BlockPickerPortal />}
            {/* Kiosk behaviour for an always-on operational screen, and the
                confirm modal a txn_action step's button may ask for. */}
            {runtime.mode !== 'document' && !runtime.reorganizeMode && !simulatedViewport
              && <TerminalMode config={page.chrome?.terminal} />}
            {actionConfirmModal}
          </PageVarsContext.Provider>
        </PageFieldRefsContext.Provider>
      </PageFieldErrorsContext.Provider>
    </PageRuntimeContext.Provider>
    </ReaderApiContext.Provider>
    </PageDataBlocksContext.Provider>
  )
}

// ─── Action runtime ─────────────────────────────────
// Mirrors PageDesignerPage's runtime construction (the canonical
// implementation) but reads/writes the smart-page RuntimeContext's
// page-vars store instead of a private inputValues state, so action
// effects (set_input, clear_inputs) flow through the same store that
// embed selection and input controls use. Returns the PageRuntime plus
// the field-error / field-ref APIs and a {{var}} resolver.

interface ActionRuntimeBundle {
  pageRuntime: PageRuntime
  fieldErrorsApi: { errors: FieldErrors; setError: (n: string, m: string) => void; clearError: (n: string) => void; clearAll: () => void }
  fieldRefsApi: { register: (n: string, el: HTMLElement | null) => void; focus: (n: string) => boolean }
  varsResolver: (key: string) => unknown
  actionConfirmModal: React.ReactNode
}

/** A record's column, as authored (`STATUS`) or as the engine keys it (`status`). */
function readColumn(row: Record<string, unknown>, column: string): unknown {
  if (column in row) return row[column]
  const lc = column.toLowerCase()
  for (const k of Object.keys(row)) if (k.toLowerCase() === lc) return row[k]
  return getPath(row, column)
}

function useActionRuntime(): ActionRuntimeBundle {
  const runtime = useRuntime()
  const { setVar, setVars } = runtime

  const [actionState, setActionState] = useState<Record<string, ActionState>>({})
  const actionStateRef = useRef(actionState)
  // The page resolver (`me.*`, data-source tokens) for action steps; set below
  // once it is built, read when an action runs.
  const varsResolverRef = useRef<(key: string) => unknown>(() => undefined)
  actionStateRef.current = actionState

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const fieldRefsRef = useRef<Map<string, HTMLElement>>(new Map())
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())

  // Latest snapshots — actions are async, so closures must read fresh state.
  const varsRef = useRef(runtime.vars)
  varsRef.current = runtime.vars
  const pageRef = useRef(runtime.page)
  pageRef.current = runtime.page
  const projectRef = useRef(runtime.project)
  projectRef.current = runtime.project
  const dataAccessRef = useRef(runtime.dataAccess)
  dataAccessRef.current = runtime.dataAccess
  const refreshSourcesRef = useRef(runtime.refreshSources)
  refreshSourcesRef.current = runtime.refreshSources
  // The shared action-confirmation modal (password / OTP / "are you sure")
  // for a txn_action step whose button asks for one.
  const { confirmAction, actionConfirmModal } = useActionConfirm()

  const setActionStateOne = useCallback((name: string, st: ActionState) => {
    setActionState((prev) => ({ ...prev, [name]: st }))
  }, [])
  const setInputCb = useCallback((name: string, value: string) => {
    setVar(name, value)
  }, [setVar])
  const setVarRawCb = useCallback((name: string, value: unknown) => {
    setVar(name, value)
  }, [setVar])
  const clearInputsCb = useCallback((names?: string[]) => {
    const list = names && names.length > 0
      ? names
      : (pageRef.current.inputs || []).map((i) => i.name)
    const patch: Record<string, unknown> = {}
    for (const n of list) patch[n] = ''
    setVars(patch)
  }, [setVars])
  const setFieldErrorCb = useCallback((name: string, message: string) => {
    setFieldErrors((prev) => ({ ...prev, [name]: message }))
  }, [])
  const clearFieldErrorCb = useCallback((name: string) => {
    setFieldErrors((prev) => {
      if (!(name in prev)) return prev
      const next = { ...prev }
      delete next[name]
      return next
    })
  }, [])
  const clearAllFieldErrorsCb = useCallback(() => setFieldErrors({}), [])
  const focusInputCb = useCallback((name: string) => {
    const el = fieldRefsRef.current.get(name)
    if (!el) {
      if (import.meta.env.DEV) console.warn(`[smartpage] focus_input: '${name}' is not mounted`)
      return false
    }
    el.focus?.()
    el.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    return true
  }, [])
  const showMessageCb = useCallback((level: 'success' | 'error' | 'info', text: string) => {
    if (level === 'success') toast.success(text)
    else if (level === 'error') toast.error(text)
    else toast(text)
  }, [])
  const navigateToCb = useCallback((objType: string, objName: string) => {
    if (!objName) return
    // The embedded smart-page runtime has no router of its own — emit a
    // CANCELABLE event the host (Run nav shell / site viewer) listens for and
    // claims via preventDefault. dispatchEvent returns false when a listener
    // called preventDefault (handled); true means nothing routed it.
    if (typeof window !== 'undefined') {
      const ev = new CustomEvent('smartpage:navigate', {
        detail: { objType, objName },
        cancelable: true,
      })
      const unhandled = window.dispatchEvent(ev)
      if (unhandled) {
        // No host routed it (e.g. a bare designer preview pane) — tell the user
        // instead of failing silently.
        toast(`Open this page in Run to navigate to "${objName}".`)
      }
    }
  }, [])

  const runActionByNameCb = useCallback(async (name: string, scope?: VarSource, extra?: Record<string, unknown>) => {
    const action = (pageRef.current.actions || []).find((a) => a.name === name)
    if (!action) {
      toast.error(`Action "${name}" not found`)
      return
    }
    // A page-level button is guarded against a double press. An action run
    // FOR A ROW (a tap on one ticket line) is not: tapping the next line while
    // the first is saving must work.
    if (!scope && actionStateRef.current[name]?.loading) return  // already running
    clearAllFieldErrorsCb()

    const ac = new AbortController()
    const acKey = scope ? `${name}#${Math.random().toString(36).slice(2)}` : name
    abortControllersRef.current.set(acKey, ac)

    const resolve = buildResolver({
      values: varsRef.current,
      pageMeta: {
        title: pageRef.current.title || '',
        description: pageRef.current.description || '',
        'page.title': pageRef.current.title || '',
        'page.description': pageRef.current.description || '',
      },
      actionState: actionStateRef.current,
      fallback: (key: string) => varsResolverRef.current(key),
      scope,
      extra,
    })

    const ctx: ActionCtx = {
      inputs: pageRef.current.inputs || [],
      values: varsRef.current,
      setInput: setInputCb,
      setVarRaw: setVarRawCb,
      clearInputs: clearInputsCb,
      setFieldError: setFieldErrorCb,
      clearFieldError: clearFieldErrorCb,
      focusInput: focusInputCb,
      setActionState: setActionStateOne,
      getActionState: (k) => actionStateRef.current[k],
      resolve,
      showMessage: showMessageCb,
      navigate: navigateToCb,
      signal: ac.signal,
      // IOFlow-routed call_api steps go through the platform invoke proxy.
      integrationInvokeUrl: (apiName) =>
        `${APP_BASE}/api/integration/invoke/${encodeURIComponent(projectRef.current)}/${encodeURIComponent(apiName)}`,
      // Record steps run through the same engine transport an embedded
      // transaction uses here (live-preview for the Run view / preview,
      // site-txn for the published site viewer).
      execute: (objName, act, payload) =>
        embedExecute(dataAccessRef.current, projectRef.current, objName, act, payload),
      refreshSources: (ids) => refreshSourcesRef.current(ids),
      confirmAction,
    }
    try {
      await runAction(action, ctx)
    } finally {
      abortControllersRef.current.delete(acKey)
    }
  }, [
    setInputCb, setVarRawCb, clearInputsCb, setFieldErrorCb, clearFieldErrorCb, focusInputCb,
    setActionStateOne, showMessageCb, navigateToCb, clearAllFieldErrorsCb, confirmAction,
  ])

  // Arrivals on a live source may run a page action (`onNewRows.action`).
  const { setLiveActionRunner } = runtime
  useEffect(() => {
    setLiveActionRunner((name, extra) => { void runActionByNameCb(name, undefined, extra) })
    return () => setLiveActionRunner(null)
  }, [setLiveActionRunner, runActionByNameCb])

  // Abort any in-flight actions on unmount.
  useEffect(() => {
    const controllers = abortControllersRef.current
    return () => {
      for (const ac of controllers.values()) ac.abort()
      controllers.clear()
    }
  }, [])

  const pageRuntime = useMemo<PageRuntime>(() => ({
    runActionByName: runActionByNameCb,
    getActionState: (k) => actionStateRef.current[k],
    setInput: setInputCb,
    clearInputs: clearInputsCb,
    navigateTo: navigateToCb,
    showMessage: showMessageCb,
    getInputs: () => pageRef.current.inputs || [],
    getInputValues: () => varsRef.current,
    getActions: () => pageRef.current.actions || [],
    getProject: () => projectRef.current,
  }), [runActionByNameCb, setInputCb, clearInputsCb, navigateToCb, showMessageCb])

  const fieldErrorsApi = useMemo(() => ({
    errors: fieldErrors,
    setError: setFieldErrorCb,
    clearError: clearFieldErrorCb,
    clearAll: clearAllFieldErrorsCb,
  }), [fieldErrors, setFieldErrorCb, clearFieldErrorCb, clearAllFieldErrorsCb])

  const fieldRefsApi = useMemo(() => ({
    register: (name: string, el: HTMLElement | null) => {
      if (el) fieldRefsRef.current.set(name, el)
      else fieldRefsRef.current.delete(name)
    },
    focus: focusInputCb,
  }), [focusInputCb])

  // Resolver covers page vars first, then page meta, then action-state
  // lookups ({{action.loading|error|status}} and {{action.<json.path>}}).
  // Depends on `vars` + `actionState` so the context value changes identity
  // when they do, re-rendering substitution consumers in renderers.tsx.
  const vars = runtime.vars
  // `{{me.*}}` — the signed-in visitor (page-runtime/visibility.ts), from the
  // same session the auth_menu shows.
  const { resources, page: runtimePage } = runtime
  const dataSourceIds = useMemo(
    () => new Set((runtimePage?.dataSources || []).map((d) => d.id).filter(Boolean) as string[]),
    [runtimePage?.dataSources],
  )
  const me = meOf(useAuthMenu())
  const meKey = `${me.signedIn}|${me.email}|${me.name}|${me.roles}`
  const varsResolver = useCallback((key: string): unknown => {
    if (Object.prototype.hasOwnProperty.call(vars, key)) return vars[key]
    if (key === 'me' || key.startsWith('me.')) return (withMe(undefined, me) as (k: string) => unknown)(key)
    if (key === 'title' || key === 'page.title') return pageRef.current.title || ''
    if (key === 'description' || key === 'page.description') return pageRef.current.description || ''
    const dot = key.indexOf('.')
    if (dot > 0) {
      const head = key.slice(0, dot)
      const tail = key.slice(dot + 1)
      const st = actionState[head]
      if (st) {
        if (tail === 'loading') return st.loading ? 'true' : 'false'
        if (tail === 'error') return st.error || ''
        if (tail === 'status') return st.status === undefined ? '' : String(st.status)
        const val = getPath(st.response, tail)
        return val === undefined ? undefined : toDisplayString(val)
      }
      // Action referenced before it has ever run (no state yet): resolve the
      // state fields to stable empties so `{{action.error}}` /
      // `{{action.loading}}` render blank instead of leaking the literal
      // placeholder (Angular parity). Skip when `head` is a page var — those
      // dotted tokens are JSON-path walks handled by substituteVars.
      if (!Object.prototype.hasOwnProperty.call(vars, head)) {
        if (tail === 'loading') return 'false'
        if (tail === 'error') return ''
        if (tail === 'status') return ''
      }
    }
    const st = actionState[key]
    if (st) return toDisplayString(st.response)
    // `{{<dataSourceId>.<column>}}` — a page data source, so TEXT and
    // visibility conditions can read the record it resolved (e.g. show a
    // block only while `{{ds_app.STATUS}} == 'PENDING'`). A source the page
    // declares but that has not loaded, or found no row, reads as '' — never
    // as an unresolved token, which a condition treats as "visible".
    // `{{<id>._loaded}}` says whether the answer has arrived yet.
    if (dot > 0) {
      const head = key.slice(0, dot)
      if (dataSourceIds.has(head)) {
        const entry = resources[`ds:${head}`] as { row?: unknown; rows?: unknown[] } | undefined
        // `{{ds_x._loaded}}` — gate a block on the answer having ARRIVED, so
        // "no record" is never shown while the record is still loading.
        if (key.slice(dot + 1) === '_loaded') return entry ? 'true' : 'false'
        const row = entry?.row ?? (Array.isArray(entry?.rows) ? entry?.rows[0] : undefined)
        const val = row && typeof row === 'object'
          ? readColumn(row as Record<string, unknown>, key.slice(dot + 1)) : undefined
        return val == null ? '' : toDisplayString(val)
      }
    }
    return undefined
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `me` is keyed by meKey
  }, [vars, actionState, meKey, resources, dataSourceIds])

  varsResolverRef.current = varsResolver

  return { pageRuntime, fieldErrorsApi, fieldRefsApi, varsResolver, actionConfirmModal }
}

// Header bar for document mode — surfaces sync state, a manual Save
// button, and a 3-dot kebab menu containing the Reorganize toggle.
// Mirrors the Angular smart-page runtime's header.
function DocHeader() {
  const runtime = useRuntime()
  const { syncState, reorganizeMode, setReorganizeMode, flushSaves } = runtime
  const [menuOpen, setMenuOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  // Close the menu when clicking outside.
  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => { document.removeEventListener('mousedown', handler) }
  }, [menuOpen])

  const handleSave = async () => {
    setSaving(true)
    try {
      await flushSaves()
    } finally {
      setSaving(false)
    }
  }

  const saveDisabled = saving || (!syncState.pending && !syncState.lastSavedAt)
  const statusText = saving || syncState.pending
    ? 'Saving…'
    : syncState.lastError
      ? 'Save failed'
      : syncState.lastSavedAt
        ? `Saved ${formatRelativeTime(syncState.lastSavedAt)}`
        : ''

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end',
      padding: '6px 0 10px',
      borderBottom: '1px solid var(--color-border)', marginBottom: 12,
    }}>
      {reorganizeMode && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 8px', borderRadius: 999,
          fontSize: 12, fontWeight: 600,
          color: 'var(--color-primary)', background: 'rgba(0, 97, 208, 0.10)',
          border: '1px solid rgba(0, 97, 208, 0.30)',
        }}>
          <Pencil size={11} />
          Reorganize mode
        </span>
      )}
      <span style={{
        fontSize: 12,
        color: syncState.lastError ? '#dc2626' : 'var(--color-text-secondary)',
        minWidth: 0,
      }}>
        {statusText}
      </span>
      <button
        type="button"
        className="btn btn-primary btn-sm"
        disabled={saveDisabled}
        onClick={handleSave}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        {saving ? <Loader2 size={12} className="spin" /> : <Check size={12} />}
        Save
      </button>
      <div ref={menuRef} style={{ position: 'relative' }}>
        <button
          type="button"
          aria-label="Document actions"
          onClick={() => setMenuOpen((v) => !v)}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: 6,
            background: 'transparent', color: 'var(--color-text-secondary)',
            border: '1px solid var(--color-border)', cursor: 'pointer',
          }}
        >
          <MoreVertical size={14} />
        </button>
        {menuOpen && (
          <div
            role="menu"
            style={{
              position: 'absolute', right: 0, top: 'calc(100% + 4px)',
              minWidth: 180, zIndex: 5,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)', borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: 4,
            }}
          >
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={reorganizeMode}
              onClick={async () => {
                const next = !reorganizeMode
                setReorganizeMode(next)
                setMenuOpen(false)
                // Leaving reorganize: flush pending edits so the user
                // sees a clean settled state.
                if (!next) {
                  try { await flushSaves() } catch { /* toasted */ }
                }
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '8px 10px', borderRadius: 4,
                background: reorganizeMode ? 'var(--color-bg-secondary)' : 'transparent',
                border: 'none', cursor: 'pointer', textAlign: 'left',
                color: 'var(--color-text)', fontSize: 13,
              }}
            >
              <Pencil size={13} />
              <span style={{ flex: 1 }}>Reorganize</span>
              {reorganizeMode && <Check size={13} color="var(--color-primary)" />}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// Compact "x seconds/minutes/hours ago" for the save indicator.
function formatRelativeTime(ts: number): string {
  const diffMs = Date.now() - ts
  if (diffMs < 5_000) return 'just now'
  if (diffMs < 60_000) return `${Math.round(diffMs / 1000)}s ago`
  if (diffMs < 3_600_000) return `${Math.round(diffMs / 60_000)}m ago`
  return new Date(ts).toLocaleTimeString()
}

function RuntimePageTree({ page }: { page: PageModel }) {
  const chrome = React.useContext(SectionChromeContext)
  if (page.sections && page.sections.length > 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--pg-sec-gap)', ['--pg-sec-gap' as never]: '16px' }}>
        {page.sections.map((sec, i) => chrome
          ? <React.Fragment key={sec.id}>{chrome(sec, i, <SectionRender sec={sec} />)}</React.Fragment>
          : <SectionRender key={sec.id} sec={sec} />)}
      </div>
    )
  }
  return <RootRender blocks={page.blocks} />
}

/** Load the page theme's webfonts (the SSR writes them into <head>). */
function PageThemeFonts({ theme }: { theme: PageModel['theme'] }) {
  useEffect(() => {
    try {
      ensureThemeFontsLoaded(resolveTheme(theme))
    } catch (err) {
      console.warn('[SmartPageRuntime] theme font load skipped', err)
    }
  }, [theme])
  return null
}

function SectionRender({ sec }: { sec: Section }) {
  const runtime = useRuntime()
  const visibilityVars = useVisibilityVars()
  const fullBleed = React.useContext(FullBleedContext)
  const simulatedViewport = React.useContext(SimulatedViewportContext)
  const entries = useEntriesFor(sec.id, sec.blocks)
  const entryIds = useMemo(() => entries.map((e) => e.id), [entries])
  // Section-level visibility gate (reorganize mode shows all).
  if (
    !runtime.reorganizeMode &&
    !isVisible(sec.visibility, { roles: runtime.userRoles, vars: visibilityVars, viewport: simulatedViewport })
  ) {
    return null
  }
  const scheme = resolveColorScheme(runtime.page.designTokens, sec.colorSchemeId)
  const gap = typeof sec.blockGap === 'number' ? sec.blockGap : 12

  const isPopup = sec.tag === 'popup'

  // Pop-up section in preview/run mode: render a floating trigger that opens
  // the section's blocks in a modal. The blocks render INSIDE the dialog (not
  // in the page flow). In reorganize/edit mode we fall through to the normal
  // inline rendering so the author can add/edit those blocks.
  if (isPopup && !runtime.reorganizeMode) {
    const popup = sec.popup
    if (!popup) return null
    return (
      <PopupLauncher
        trigger={popup.trigger}
        modalTitle={popup.modalTitle}
        modalSize={popup.modalSize}
        modalPosition={popup.modalPosition}
        animation={popup.animation}
        themeVars={scheme ? (colorSchemeToCssVars(scheme) as Record<string, string>) : undefined}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>
          {entries.map((entry) => (
            <RuntimeBlockHost key={entry.id} block={entry.block} scope={sec.id} />
          ))}
        </div>
      </PopupLauncher>
    )
  }

  // The layout primitives (full-bleed / breakout width, viewport height,
  // dividers, decorations, sticky, anchor id) come from the SAME
  // SectionWrapper the SSR and the standalone runtime use, so a page looks
  // the same here as published. Only where the page is not the viewport
  // (a designer pane, a thumbnail) is a `full`/`breakout` band kept inside
  // the column — `100vw` there would overflow the pane.
  const layoutSec: Section = !fullBleed && sec.width && sec.width !== 'contained'
    ? { ...sec, width: 'contained' }
    : sec
  const wrapped: Section = typeof layoutSec.blockGap === 'number' ? layoutSec : { ...layoutSec, blockGap: gap }

  return (
    <SectionWrapper section={wrapped} designTokens={runtime.page.designTokens}>
      {isPopup && (
        <div style={{
          padding: '6px 10px', borderRadius: 6, fontSize: 12,
          background: 'rgba(20,184,166,0.12)', color: 'var(--pg-text, #0f172a)',
          border: '1px dashed #14b8a6',
        }}>
          🪟 Pop-up section — these blocks open in a modal dialog at runtime. Configure the trigger in the section settings.
        </div>
      )}
      {runtime.reorganizeMode && (
        <AddBlockRow scope={sec.id} afterBlockId={null} parentBlock={null} currentOrder={entryIds} />
      )}
      {entries.map((entry) => (
        <React.Fragment key={entry.id}>
          <RuntimeBlockHost block={entry.block} scope={sec.id} />
          {runtime.reorganizeMode && (
            <AddBlockRow scope={sec.id} afterBlockId={entry.id} parentBlock={null} currentOrder={entryIds} />
          )}
        </React.Fragment>
      ))}
    </SectionWrapper>
  )
}

function RootRender({ blocks }: { blocks: Block[] }) {
  const runtime = useRuntime()
  const entries = useEntriesFor(ROOT_SCOPE, blocks)
  const entryIds = useMemo(() => entries.map((e) => e.id), [entries])
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {runtime.reorganizeMode && (
        <AddBlockRow scope={ROOT_SCOPE} afterBlockId={null} parentBlock={null} currentOrder={entryIds} />
      )}
      {entries.map((entry) => (
        <React.Fragment key={entry.id}>
          <RuntimeBlockHost block={entry.block} scope={ROOT_SCOPE} />
          {runtime.reorganizeMode && (
            <AddBlockRow scope={ROOT_SCOPE} afterBlockId={entry.id} parentBlock={null} currentOrder={entryIds} />
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

// ─── Helpers ───────────────────────────────────────

function collectBlockIds(page: PageModel): Set<string> {
  const ids = new Set<string>()
  const seed = page.sections
    ? page.sections.flatMap((s) => s.blocks)
    : page.blocks
  const stack = [...seed]
  while (stack.length) {
    const b = stack.shift()!
    ids.add(b.id)
    if (b.children && b.children.length) stack.push(...b.children)
  }
  return ids
}

function RuntimeLoadingBox({ label }: { label: string }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 32,
      gap: 12,
      color: 'var(--color-text-secondary)',
      fontSize: 13,
    }}>
      <Loader2 size={16} className="spin" />
      {label}
    </div>
  )
}

function RuntimeErrorBox({ message }: { message: string }) {
  return (
    <div style={{
      padding: 16,
      border: '1px solid var(--color-error, #dc2626)',
      borderRadius: 6,
      color: 'var(--color-error, #dc2626)',
      fontSize: 13,
    }}>
      {message}
    </div>
  )
}
